import {
  GetAccount200Response,
  GetAccount200ResponsePositionsInner,
  SideResponse,
} from "../codegen-api";
import { sumKnown } from "./unknownNumbers";

const DEFAULT_MAINTENANCE_RATE = 0.05;
const EPSILON = 1e-12;

const num = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const sideSign = (side?: SideResponse) => (side === SideResponse.Sell ? -1 : 1);

const pnlAtPrice = (
  side: SideResponse,
  amount: number,
  entryPrice: number,
  price: number
) =>
  side === SideResponse.Buy
    ? (price - entryPrice) * amount
    : (entryPrice - price) * amount;

export interface LiquidationPreviewInput {
  account?: Partial<
    Pick<
      GetAccount200Response,
      "balance" | "collaterals" | "equity" | "maintenance_margin" | "positions"
    >
  >;
  currentPosition?: GetAccount200ResponsePositionsInner;
  orderSide: SideResponse;
  orderSize: number;
  executionPrice: number;
  orderNotional?: number;
  markPrice?: number;
  estimatedFee?: number;
  maxLeverage?: number;
}

export interface LiquidationPreviewResult {
  liquidationPrice?: number;
  side: SideResponse;
  amount: number;
  avgEntryPrice: number;
  maintenanceMargin: number;
  maintenanceRate: number;
}

const inferMaintenanceRate = (
  currentPosition?: GetAccount200ResponsePositionsInner,
  maxLeverage?: number
) => {
  const currentNotional =
    num(currentPosition?.amount) * num(currentPosition?.avg_entry_price);
  const currentMaintenance = num(currentPosition?.maintenance_margin);
  if (currentNotional > 0 && currentMaintenance > 0) {
    return currentMaintenance / currentNotional;
  }

  if (maxLeverage && Number.isFinite(maxLeverage) && maxLeverage > 0) {
    return 1 / (2 * maxLeverage);
  }

  return DEFAULT_MAINTENANCE_RATE;
};

const positionNotional = (position?: GetAccount200ResponsePositionsInner) =>
  num(position?.amount) * num(position?.avg_entry_price);

const isSamePosition = (
  position?: GetAccount200ResponsePositionsInner,
  currentPosition?: GetAccount200ResponsePositionsInner
) =>
  !!position &&
  !!currentPosition &&
  (position.instrument_id === currentPosition.instrument_id ||
    position.instrument_name === currentPosition.instrument_name);

const accountCollateralValue = (
  account?: LiquidationPreviewInput["account"]
) => {
  const collateralValue = account?.collaterals?.reduce(
    (sum, collateral) => sum + num(collateral.collateral_value),
    0
  );

  if (collateralValue && collateralValue > 0) return collateralValue;

  const balance = num(account?.balance);
  if (balance > 0) return balance;

  return num(account?.equity);
};

const positivePendingFees = (position?: GetAccount200ResponsePositionsInner) =>
  Math.max(
    0,
    num(position?.pending_close_funding_fee ?? position?.pending_funding_fee)
  );

const accountEquityFromCrossHealth = (
  account: LiquidationPreviewInput["account"]
) => {
  const maintenance = num(account?.maintenance_margin);
  if (maintenance <= 0) return undefined;

  const health = account?.positions
    ?.map((position) => num(position.cross_margin_health))
    .find((value) => value > 0);

  if (!health) return undefined;

  return (maintenance * health) / 100;
};

const fallbackAccountTerm = (
  account: LiquidationPreviewInput["account"],
  currentPosition?: GetAccount200ResponsePositionsInner
): number | undefined => {
  if (!currentPosition) {
    const crossEquity = accountEquityFromCrossHealth(account);
    if (crossEquity !== undefined) return crossEquity;
  }

  const positions = account?.positions || [];
  // Another position's unrealized PnL feeds this account's equity. When one is
  // unknown (null: its market could not be resolved) the equity is unknown too,
  // and so is any liquidation price derived from it — treating it as 0 would
  // quote a precise-looking but invented price.
  const otherPnls = positions
    .filter((position) => !isSamePosition(position, currentPosition))
    .map((position) => position.unrealized_pnl);
  const fixedPnl = sumKnown(otherPnls);
  if (fixedPnl === undefined) return undefined;
  const pendingFees = positions.reduce(
    (sum, position) => sum + positivePendingFees(position),
    0
  );

  return accountCollateralValue(account) + fixedPnl - pendingFees;
};

const accountTermFromCurrentLiquidation = (
  account: LiquidationPreviewInput["account"],
  currentPosition?: GetAccount200ResponsePositionsInner
) => {
  const currentAmount = num(currentPosition?.amount);
  const currentNotional = positionNotional(currentPosition);
  const currentLiq = num(currentPosition?.liquidation_price);
  const currentSide = currentPosition?.side;

  if (
    !currentSide ||
    currentAmount <= 0 ||
    currentNotional <= 0 ||
    currentLiq <= 0
  ) {
    return undefined;
  }

  const targetSlope = currentAmount * sideSign(currentSide);
  const targetIntercept =
    currentSide === SideResponse.Buy ? -currentNotional : currentNotional;

  if (Math.abs(targetSlope) <= EPSILON) return undefined;

  // PositionLens: price = (maintenance - accountTerm - targetIntercept) / targetSlope.
  return num(account?.maintenance_margin) - targetIntercept - currentLiq * targetSlope;
};

export const estimateProjectedLiquidationPrice = ({
  account,
  currentPosition,
  orderSide,
  orderSize,
  executionPrice,
  orderNotional,
  markPrice,
  estimatedFee = 0,
  maxLeverage,
}: LiquidationPreviewInput): LiquidationPreviewResult | undefined => {
  const size = num(orderSize);
  const entry = num(executionPrice);
  if (size <= 0 || entry <= 0) return undefined;

  const executableOrderNotional = num(orderNotional) || size * entry;
  if (executableOrderNotional <= 0) return undefined;

  const currentAmount = num(currentPosition?.amount);
  const currentEntry = num(currentPosition?.avg_entry_price);
  const currentSide = currentPosition?.side;
  const currentSign = currentSide ? sideSign(currentSide) : 0;
  const orderSign = sideSign(orderSide);
  const currentSignedSize = currentAmount * currentSign;
  const nextSignedSize = currentSignedSize + size * orderSign;
  const nextAmount = Math.abs(nextSignedSize);

  if (nextAmount <= EPSILON) return undefined;

  const nextSide =
    nextSignedSize >= 0 ? SideResponse.Buy : SideResponse.Sell;

  let nextEntry = entry;
  let realizedOnClosedSize = 0;
  let nextNotional = executableOrderNotional;

  if (currentAmount > 0 && currentSide && currentEntry > 0) {
    if (currentSign === orderSign) {
      nextNotional = positionNotional(currentPosition) + executableOrderNotional;
      nextEntry =
        nextNotional / nextAmount;
    } else {
      const closedSize = Math.min(currentAmount, size);
      realizedOnClosedSize = pnlAtPrice(
        currentSide,
        closedSize,
        currentEntry,
        entry
      );
      if (Math.abs(nextSignedSize) > EPSILON && Math.abs(size - currentAmount) < EPSILON) {
        nextEntry = currentEntry;
        nextNotional = nextAmount * nextEntry;
      } else if (Math.abs(nextSignedSize) > EPSILON && size < currentAmount) {
        nextEntry = currentEntry;
        nextNotional = nextAmount * nextEntry;
      } else {
        nextEntry = entry;
        nextNotional = nextAmount * entry;
      }
    }
  }

  if (!Number.isFinite(nextEntry) || nextEntry <= 0) return undefined;
  if (!Number.isFinite(nextNotional) || nextNotional <= 0) return undefined;

  const maintenanceRate = inferMaintenanceRate(currentPosition, maxLeverage);
  const currentMaintenance = num(currentPosition?.maintenance_margin);
  const nextMaintenance = nextNotional * maintenanceRate;
  const projectedMaintenance =
    Math.max(0, num(account?.maintenance_margin) - currentMaintenance) +
    nextMaintenance;
  const accountTerm =
    accountTermFromCurrentLiquidation(account, currentPosition) ??
    fallbackAccountTerm(account, currentPosition);
  const targetSlope = nextAmount * sideSign(nextSide);
  const targetIntercept =
    nextSide === SideResponse.Buy ? -nextNotional : nextNotional;

  if (Math.abs(targetSlope) <= EPSILON) return undefined;

  // Unknown account equity: the position preview still stands, the
  // liquidation price does not.
  let liquidationPrice: number | undefined =
    accountTerm === undefined
      ? undefined
      : (projectedMaintenance -
          (accountTerm + realizedOnClosedSize - num(estimatedFee)) -
          targetIntercept) /
        targetSlope;

  if (
    liquidationPrice === undefined ||
    !Number.isFinite(liquidationPrice) ||
    liquidationPrice <= 0
  ) {
    liquidationPrice = undefined;
  }

  return {
    liquidationPrice,
    side: nextSide,
    amount: nextAmount,
    avgEntryPrice: nextEntry,
    maintenanceMargin: nextMaintenance,
    maintenanceRate,
  };
};
